/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type AdobeMetrics from './AdobeMetrics';
import type {EventData, PageData, UserData} from './DigitalData';
import {Level} from './Level';
import type MetricsApi from './MetricsApi';
import type Record from './Record';
import {RecordType} from './RecordType';
import {BUILD_VERSION as sdkVersion} from './BuildVersion';
import type Timer from './Timer';

/**
 * Metrics class is used for sending instrumentation data of all types to remote
 * storage. Use of this Metrics class must be preceeded by proper construction
 * and initialization of a metrics runtime class in order to transmit metrics to
 * remote storage.
 *
 * @property {string} name The name for this Metrics instance. It is recommended
 * to use a logger-style name in the form of path.to.my.package.MyClassName to
 * aid searching and filtering of stored metrics, and to be consistent with
 * future features for filtering emitted metrics based on package, class and
 * log level.
 * @see MetricsBrowserRuntime
 * @see MetricsNodeJsRuntime
 */
export default class Metrics implements MetricsApi {
  public static version = sdkVersion;
  public context: any;
  public name: string;

  /**
   * Queue a record for transmission to remote storage.
   *
   * @param {Record} record - The object to be stored.
   */
  public readonly store: (record: Record) => void;

  private readonly _log: (
    recordType: RecordType,
    level: Level,
    name: string,
    message: string | undefined,
    event: string | string[] | undefined,
    args: any[],
  ) => Promise<number>;

  /**
   * Construct a Metrics object. The name of the Metrics object should give
   * context of what is being measured, typically the name of the class that
   * owns the Metrics object. It may be appropriate in some circumstances to
   * scope name more narrowly, for instance to the function name. The remote
   * storage is multitenant, so it is never appropriate to use a generic name
   * such as "MyApp".
   *
   * Optional arguments may be supplied. These properties are considered state
   * which will be attached to the emitted metrics. A common use case is for
   * labels, categories or tags.
   *
   * @param {string} name The name gives context of what is being measured.
   * @param {AdobeMetrics} adobeMetrics Adobe Metrics Object.
   * @param {any} args Optional arguments that will be written into the
   * metricsState context bag on the recorded metrics.
   */
  public constructor(name: string, adobeMetrics: AdobeMetrics, ...args: any);
  public constructor(name: string, ...args: any);
  public constructor(name: string, ...args: any) {
    this.name = name;
    let adobeMetrics: AdobeMetrics;
    if (args.length && typeof args[0] === 'object' && typeof args[0].now === 'function') {
      adobeMetrics = args[0];
      args.shift();
    } else {
      adobeMetrics = (global as any).adobeMetrics;
    }

    this._log = (
      recordType: RecordType,
      level: Level,
      name: string,
      message: string | undefined,
      event: string | string[] | undefined,
      args: any[],
    ): Promise<number> =>
      adobeMetrics.write({
        data: args.length > 0 ? [...args] : undefined,
        event,
        level,
        message,
        metricsState: {sdkVersion},
        name,
        recordType,
      } as Record);

    this.store = (record: Record): void => {
      record.metricsState = {sdkVersion};
      record.name = this.name;
      adobeMetrics.write(record);
    };

    switch (args.length) {
      case 0:
        break;
      case 1:
        this.context = args[0];
        break;
      default:
        this.context = args;
        break;
    }
  }

  /**
   * Log an ERROR message to remote storage, with an optional
   * data payload; emulates console.error().
   *
   * @function
   * @param {string} message - The log message.
   * @param {any} args Optional arguments to be applied to the recorded metrics.
   * @returns {Promise} A promise that resolves to the number of metrics that
   * were queued for eventual flushing.
   */
  public error(message: string, ...args: any): Promise<number> {
    return this._log(RecordType.LOG, Level.ERROR, this.name, message, undefined, args);
  }

  /**
   * Log a WARN message to remote storage, with an optional data payload;
   * emulates console.warn().
   *
   * @function
   * @param {string} message - The log message.
   * @param {any} args Optional arguments to be applied to the recorded metrics.
   * @returns {Promise} A promise that resolves to the number of metrics that
   * were queued for eventual flushing.
   */
  public warn(message: string, ...args: any): Promise<number> {
    return this._log(RecordType.LOG, Level.WARN, this.name, message, undefined, args);
  }

  /**
   * Log an INFO message to remote storage, with an optional data payload;
   * emulates console.log().
   *
   * @function
   * @param {string} message - The log message.
   * @param {any} args Optional arguments to be applied to the recorded metrics.
   * @returns {Promise} A promise that resolves to the number of metrics that
   * were queued for eventual flushing.
   */
  public log(message: string, ...args: any): Promise<number> {
    return this._log(RecordType.LOG, Level.INFO, this.name, message, undefined, args);
  }

  /**
   * Log an INFO message to remote storage, with an optional data payload;
   * emulates console.info().
   *
   * @function
   * @param {string} message - The log message.
   * @param {any} args Optional arguments to be applied to the recorded metrics.
   * @returns {Promise} A promise that resolves to the number of metrics that
   * were queued for eventual flushing.
   */
  public info(message: string, ...args: any): Promise<number> {
    return this._log(RecordType.LOG, Level.INFO, this.name, message, undefined, args);
  }

  /**
   * Log a DEBUG message to remote storage, with an optional data payload;
   * emulates console.debug().
   *
   * @function
   * @param {string} message - The log message.
   * @param {any} args Optional arguments to be applied to the recorded metrics.
   * @returns {Promise} A promise that resolves to the number of metrics that
   * were queued for eventual flushing.
   */
  public debug(message: string, ...args: any): Promise<number> {
    return this._log(RecordType.LOG, Level.DEBUG, this.name, message, undefined, args);
  }

  /**
   * Log a TRACE message to remote storage, with an optional data payload;
   * emulates console.trace().
   *
   * @param {string} message - The log message.
   * @param {any} args Optional arguments to be applied to the recorded metrics.
   * @returns {Promise} A promise that resolves to the number of metrics that
   * were queued for eventual flushing.
   */
  public trace(message: string, ...args: any): Promise<number> {
    return this._log(RecordType.LOG, Level.TRACE, this.name, message, undefined, args);
  }

  /**
   * Construct and return a Timer with properties as set from this Metrics
   * object plus any supplied property bags.
   *
   * Timer level will default to INFO. To specify a different
   * level, add an level object to the args parameters in the form
   * {level: Level.DEBUG}.
   *
   * @param {string} prefix The event prefix for the timer.
   * @param {any} args Optional arguments to be applied to the
   * recorded metrics.
   * @returns {Timer} The constructed Timer.
   */
  public start(prefix: string, ...args: any): Timer {
    return ((global as any).adobeMetrics as AdobeMetrics).create(
      RecordType.TIMER,
      this.name,
      this.context,
      prefix,
      ...args,
    ) as Timer;
  }

  /**
   * Construct a named event and emit it to storage, along with an optional
   * data payload. Event level will default to INFO. To specify a different
   * level, add an level object to the args parameters in the form
   * {level: Level.DEBUG}.
   *
   * @function
   * @param {string} event The event.
   * @param {any} args Optional arguments to be applied to the recorded metrics.
   * @returns {Promise} A promise that resolves to the number of metrics that
   * were queued for eventual flushing.
   */
  public event(event: string | string[], ...args: any): Promise<number> {
    return this._log(RecordType.EVENT, Level.INFO, this.name, undefined, event, args);
  }

  /**
   * Construct a named "Recent" event and emit it to storage. The payload is expected
   * to contain PII so downstream handling must a) keep the data bag intact, and
   * b) send the Recent record (with PII) only to Unified Recents. PII must be
   * removed before sending to ADX.
   *
   * @function
   * @param {string} revent The event.
   * @param {any} args Optional arguments to be applied to the recorded metrics.
   * @returns {Promise} A promise that resolves to the number of metrics that
   * were queued for eventual flushing.
   */
  public recent(revent: string | string[], ...args: any): Promise<number> {
    return this._log(RecordType.RECENT, Level.INFO, this.name, undefined, revent, args);
  }

  /**
   * Construct a named "SEA" (Solution Engagement Analytics) event and emit it to storage.
   * The payload is expected to contain User interactions from Adobe Analytics/ Customer Journey
   * Analytics.
   *
   * @param {string | string[]} seaEvent - The event or events to be logged.
   * @param {...any} args - Additional arguments to be logged with the event.
   * @returns {Promise<number>} - Returns a promise that resolves to the number of metrics
   * that were queued for eventual flushing.
   */
  public sea(seaEvent: string | string[], ...args: any): Promise<number> {
    return this._log(RecordType.SEA, Level.INFO, this.name, undefined, seaEvent, args);
  }
  /**
   * The history object emulates window history APIs for recording history
   * metrics. When using push or replace you can specify the path and state
   * as separate arguments, or specify path as a string and state as an
   * object, or put everything into one location-like argument. In all cases,
   * the resulting history record will be a location-like argument of the
   * form {path, search, hash, state}.
   *
   * If path is supplied as a string it will be disassembled into a
   * location-like object and the state parameter will be added to that
   * object if the supplied state parameter was an object.
   *
   * The resulting location-like object will be stored in the data property of
   * the metric if the prop props argument is empty. If props is not empty,
   * then the constructed location-like object will be the first object in an
   * array on the metric data property, and the remainder of the array will
   * come from processing the props parameter through makeDataBag().
   */
  public history = {
    back: (...args: any): Promise<number> => {
      return this._log(RecordType.HISTORY, Level.INFO, this.name, undefined, 'back', args);
    },
    forward: (...args: any): Promise<number> => {
      return this._log(RecordType.HISTORY, Level.INFO, this.name, undefined, 'forward', args);
    },
    go: (n: number, ...args: any): Promise<number> => {
      args.unshift({n});
      return this._log(RecordType.HISTORY, Level.INFO, this.name, undefined, 'go', args);
    },
    push: (path: any, state?: any, ...args: any): Promise<number> => {
      args.unshift({path, state});
      return this._log(RecordType.HISTORY, Level.INFO, this.name, undefined, 'push', args);
    },
    replace: (path: any, state?: any, ...args: any): Promise<number> => {
      args.unshift({path, state});
      return this._log(RecordType.HISTORY, Level.INFO, this.name, undefined, 'replace', args);
    },
  };

  /**
   * Adobe Analytics for Adobe - aka OMEGA. These APIs emulate
   * window._satellite.track() methods.
   *
   * Applications which self-manage the analytics javascript and handle
   * updating the window.digitalData object should call metrics.analytics.track()
   * immediately adjacent to calling window._satellite.track(), either before or
   * after.
   *
   * Applications which provide the analytics javascript url to Metrics Runtime
   * configuration can can call trackEvent(), trackPage() or trackUser() and
   * supply the corresponding data object as an argument. In this case, Metrics
   * will update the window digitalData object and take care of invoking
   * window._satellite.track as well as Metrics' track().
   */
  public analytics = {
    track: (type: 'event' | 'page' | 'user', ...args: any): Promise<number> => {
      return this._log(RecordType.ANALYTICS, Level.INFO, this.name, undefined, type, args);
    },
    trackEvent: (eventData: EventData, ...args: any): Promise<number> => {
      args.unshift(eventData);
      return this._log(RecordType.ANALYTICS, Level.INFO, this.name, undefined, 'Event', args);
    },
    trackPage: (pageData: PageData, ...args: any): Promise<number> => {
      args.unshift(pageData);
      return this._log(RecordType.ANALYTICS, Level.INFO, this.name, undefined, 'Page', args);
    },
    trackUser: (userData: UserData, ...args: any): Promise<number> => {
      args.unshift(userData);
      return this._log(RecordType.ANALYTICS, Level.INFO, this.name, undefined, 'User', args);
    },
  };

  /**
   * Location methods emit Location recordTypes and also triggers tracking of
   * a new page load. A new historyId will be generated, page state will
   * transition to active. Location methods should be await'ed just prior to the
   * corresponding window.location method to allow flush of any remaining
   * metrics from the current window to remote storage before the window
   * contents change.
   */
  public location = {
    /**
     * Record a location assign event.
     *
     * @param {string} url A string for the window location path being assigned.
     * @param {any} props Any other properties will be assigned to the metric
     * according to makeDataBag();.
     * @returns {Promise} A promise that resolves to the number of metrics that
     * were flushed to remote storage.
     */
    assign: (url: string, ...props: any): Promise<number> => {
      props.unshift({url});
      return this._log(RecordType.LOCATION, Level.INFO, this.name, undefined, 'assign', props);
    },

    /**
     * Record a location reload event.
     *
     * @param {boolean} force A boolean to indicate that the page was or will be
     * reloaded from the server.
     * @param {any} props Any other properties will be assigned to the metric
     * according to makeDataBag();.
     * @returns {Promise} A promise that resolves to the number of metrics that
     * were flushed to remote storage.
     */
    reload: (force: boolean, ...props: any): Promise<number> => {
      props.unshift({force});
      return this._log(RecordType.LOCATION, Level.INFO, this.name, undefined, 'reload', props);
    },

    /**
     * Record a location replace event.
     *
     * @param {string} url A string for the window location path being assigned.
     * @param {any} props Any other properties will be assigned to the metric
     * according to makeDataBag();.
     * @returns {Promise} A promise that resolves to the number of metrics that
     * were flushed to remote storage.
     */
    replace: (url: string, ...props: any): Promise<number> => {
      props.unshift({url});
      return this._log(RecordType.LOCATION, Level.INFO, this.name, undefined, 'replace', props);
    },
  };
}

(function (): void {
  const adobeMetrics = ((global as any).adobeMetrics = (global as any).adobeMetrics || {});
  adobeMetrics.queue = adobeMetrics.queue || [];
})();
